import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutGrid, Grid3X3, Columns, 
  Plus, Heart, Maximize2
} from 'lucide-react';
import './ProductPage.css';
import SEO from '../SEO';
const API_URL = 'https://api.sohanihandicrafts.com:5010';
const categoryName = "Collection";
const AdvancedProductPage = () => {
  const { catId } = useParams(); // Catches the ID from the URL
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [gridCols, setGridCols] = useState(3);
  const [isLoading, setIsLoading] = useState(true);

  // 1. Fetch Items by Category
  useEffect(() => {
    const fetchCategoryData = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/item/category/${catId}`);
        setProducts(res.data);
        
        // Use the first item to get Category details for the Hero section
        if (res.data.length > 0) {
          setCategoryInfo(res.data[0].category);
        }
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategoryData();
  }, [catId]);

  if (isLoading) {
    return (
      <div className="pdg-adv-loader">
        <motion.div 
          animate={{ rotate: 360 }} 
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="loader-spinner"
        />
        <p>Fetching Artisan Collection...</p>
      </div>
    );
  }

  return (
    <>
  <SEO 
  title={`${categoryName} Collection | Premium Handcrafted Furniture`}
  description={`Explore our trending ${categoryName} collection. Each piece is hand-carved in Jodhpur using premium sustainable materials. Ideal for luxury homes and high-end interiors.`}
  keywords={`${categoryName} furniture, buy ${categoryName} online, wholesale ${categoryName} india, jodhpur ${categoryName} manufacturer, hand carved wood furniture, artisan furniture collection`}
  url={`/product/${catId}`}
/>
    <div className="pdg-adv-wrapper">
      
      {/* 1. DYNAMIC ARCHITECTURAL HERO SECTION */}
      <section className="pdg-adv-hero">
        <div className="pdg-adv-container pdg-adv-hero-flex">
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="pdg-adv-hero-media"
          >
            {/* Show first product image or a placeholder in Hero */}
            <img 
               src={products.length > 0 ? `${API_URL}/${products[0].image}` : "https://via.placeholder.com/1200x800"} 
               alt="Category Showcase" 
            />
            <div className="pdg-adv-media-accent"></div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="pdg-adv-hero-content"
          >
            <span className="pdg-adv-overline">Artisan Series</span>
            <h1 className="pdg-adv-main-title">
                {categoryInfo ? categoryInfo.name : "Collection"} <br/>
                <span>Masterpieces</span>
            </h1>
            <p className="pdg-adv-lead-para">
                Explore our curated selection of hand-crafted items. Every piece 
                is constructed with a commitment to durability, heritage, and 
                the natural beauty of the wood grain.
            </p>
            
            <div className="pdg-adv-specs">
                <div className="pdg-adv-spec-item"><span>Status</span> <strong>In Stock</strong></div>
                <div className="pdg-adv-spec-item"><span>Origin</span> <strong>Jodhpur, India</strong></div>
            </div>

            <div className="pdg-adv-hero-actions">
                <button className="pdg-adv-btn-primary" onClick={() => window.scrollTo({top: 800, behavior: 'smooth'})}>
                    View Catalog <Plus size={18}/>
                </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. ADVANCED STICKY TOOLBAR */}
      <div className="pdg-adv-toolbar">
        <div className="pdg-adv-container pdg-adv-toolbar-inner">
          <div className="pdg-adv-toolbar-left">
            <p>Showing <strong>{products.length}</strong> Architectural Pieces</p>
          </div>

          <div className="pdg-adv-toolbar-right">
            <div className="pdg-adv-grid-toggle">
              <button onClick={() => setGridCols(2)} className={gridCols === 2 ? "active" : ""}>
                <Columns size={20}/>
              </button>
              <button onClick={() => setGridCols(3)} className={gridCols === 3 ? "active" : ""}>
                <Grid3X3 size={20}/>
              </button>
              <button onClick={() => setGridCols(4)} className={gridCols === 4 ? "active" : ""}>
                <LayoutGrid size={20}/>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 3. DYNAMIC FLUID GRID */}
      <section className="pdg-adv-gallery">
        <div className="pdg-adv-container">
          <motion.div 
            layout
            transition={{ type: "spring", stiffness: 250, damping: 30 }}
            className={`pdg-adv-product-grid pdg-adv-grid-${gridCols}`}
          >
            <AnimatePresence mode='popLayout'>
              {products.map((item) => (
                <motion.div 
                  layout
                  key={item._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="pdg-adv-product-card"
                  onClick={() => navigate(`/product-detail/${item._id}`)}
                >
                  <div className="pdg-adv-card-visual">
                    <img src={`${API_URL}/${item.image}`} alt={item.name} />
                    
                    <div className="pdg-adv-card-overlay">
                        <button className="pdg-adv-btn-quote-float">
                           View Details
                        </button>
                        <div className="pdg-adv-card-quick-icons">
                            <button><Maximize2 size={18}/></button>
                            <button onClick={(e) => {e.stopPropagation(); /* Wishlist Logic */}}><Heart size={18}/></button>
                        </div>
                    </div>

                    <span className="pdg-adv-card-label">SKU: {item.sku}</span>
                  </div>

                  <div className="pdg-adv-card-details">
                    <h3 className="pdg-adv-card-name">{item.name}</h3>
                    <div className="pdg-adv-card-line"></div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {products.length === 0 && (
            <div className="pdg-no-data">
                <p>No masterpieces found in this category yet.</p>
                <button onClick={() => navigate('/')}>Back to Home</button>
            </div>
          )}
        </div>
      </section>

    </div>
    </>
  );
};

export default AdvancedProductPage;